import React from 'react';
import { motion } from 'framer-motion';
import { Search, PenTool, Code, ShieldCheck, Rocket, LifeBuoy, Settings } from 'lucide-react';
import TechFlow from '../components/TechFlow';
import './ProcessPage.css';

const ProcessPage = () => {
  const processSteps = [
    {
      title: "Discovery",
      description: "We dive deep into your business goals, target audience, and market landscape to define the core problem and project scope.",
      icon: <Search className="step-icon" />,
      details: ["Market Research", "Competitor Analysis", "Requirement Gathering", "Project Roadmap"]
    },
    {
      title: "Planning",
      description: "Defining the technical architecture, data structures, and feature set. We set clear milestones and timelines for delivery.",
      icon: <Settings className="step-icon" />,
      details: ["System Architecture", "Tech Stack Selection", "Resource Allocation", "Timeline Definition"]
    },
    {
      title: "Design",
      description: "Creating intuitive UI/UX designs and robust system architectures. We focus on conversion and user satisfaction.",
      icon: <PenTool className="step-icon" />,
      details: ["Wireframing", "UI Design", "UX Flow", "Design System"]
    },
    {
      title: "Development",
      description: "Agile development process with regular updates. We build with clean, scalable, and maintainable code.",
      icon: <Code className="step-icon" />,
      details: ["Frontend Development", "Backend Systems", "API Integration", "Database Setup"]
    },
    {
      title: "Testing",
      description: "Rigorous quality assurance, performance optimization, and bug fixing to ensure a stable and fast application.",
      icon: <ShieldCheck className="step-icon" />,
      details: ["Unit Testing", "E2E Testing", "Performance Audit", "Security Review"]
    },
    {
      title: "Deployment",
      description: "Launching your product to production securely with automated CI/CD pipelines and cloud monitoring.",
      icon: <Rocket className="step-icon" />,
      details: ["CI/CD Pipeline", "Cloud Hosting", "SSL & Security", "Monitoring Setup"]
    },
    {
      title: "Support",
      description: "Ongoing maintenance, updates, and future improvements to ensure your product continues to evolve and scale.",
      icon: <LifeBuoy className="step-icon" />,
      details: ["Bug Fixes", "Feature Updates", "Performance Monitoring", "Scalability Support"]
    }
  ];

  return (
    <motion.div 
      className="process-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <section className="process-hero">
        <div className="container">
          <motion.h1 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
          >
            The <span>Blueprint</span> to Success
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            A systematic approach to building high-performance digital products.
          </motion.p>
        </div>
      </section>

      <section className="process-visual-section">
        <div className="container">
          <div className="visual-header">
            <h3>Infrastructure & System Flow</h3>
            <p>Our standardized technical architecture for scalable products.</p>
          </div>
          <TechFlow />
        </div>
      </section>

      <section className="process-timeline-detailed">
        <div className="container">
          <div className="ptd-container">
            {processSteps.map((step, index) => (
              <motion.div 
                key={index} 
                className={`ptd-item ${index % 2 === 0 ? 'left' : 'right'}`}
                initial={{ x: index % 2 === 0 ? -50 : 50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-100px" }}
              >
                <div className="ptd-marker">
                  <div className="marker-icon-wrapper">
                    {step.icon}
                  </div>
                  <div className="marker-number">0{index + 1}</div>
                </div>
                <div className="ptd-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                  <div className="ptd-tags">
                    {step.details.map((detail, i) => (
                      <span key={i} className="ptd-tag">{detail}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
            <div className="ptd-line"></div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default ProcessPage;
